import { ChevronRight } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/lab';
import DateAdapter from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Grid,
  LinearProgress,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  styled,
  TextField,
  CircularProgress,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import {
  createNewFeeArrangement,
  deleteFile,
  FeeArrangementDTO,
  getAllArrangementFiles,
  getClientContactAutocompleteOptions,
  getCurrencyList,
  IFeeArrangementFile,
  saveFile,
  updateFeeArrangement,
  uploadFileToS3,
  LogEntityType,
  saveActivityLog,
  LogContextType,
  LogAction,
  ourEntityAllList,
  IOurEntity,
  getFeeArrangementById,
  ClientContactDTO,
} from 'wisestep-api';
import {
  ControlledTextArea,
  ControlledTextInput,
  FileTypeVariants,
  Label,
  WsButton,
  WsColors,
  WsDatePicker,
  WsFileUpload,
  WsInputNumber,
  WsSelect,
  WsToggleButton,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import {
  getAvEditAddDesUiName,
  activityLogDTOHelper,
} from '../../../../features/activityLog/ActivityHelper';
import { useFetch } from '../../../../hooks/useFetch';
import useWsAutocomplete from '../../../../hooks/useWsAutocomplete';
import ErrorLogger from '../../../../providers/ErrorLogger';
import { getCurrencySymbol } from '../../../../utils';
import { failed, success, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { FormProps } from '../../../clientsSettings/components/FileCategoryForm';
import { getAutocompleteLabelForClientContact } from '../../../jobs/helper';
import IFeeArrangement from '../../types/IFeeArrangement';

export const getFeeArrangementFees = (feeArr?: FeeArrangementDTO) => {
  return feeArr
    ? `${
        feeArr.feeType === 'PERCENTAGE' || !feeArr.currency
          ? ''
          : getCurrencySymbol(feeArr.currency.symbol)
      }${feeArr.fee}${feeArr.feeType === 'PERCENTAGE' ? '%' : ''}`
    : '';
};

interface AutocompleteWithPersistentAddProps<T> {
  name: string;
  options: T[];
  label?: string;
  onCreate?(item): void;
  optionLabelKey?: string;
  creatable?: boolean;
  persistentAddOption?: boolean;
  required?: boolean;
  placeholder?: string;
  multiple?: boolean;
  disabled?: boolean;
  getOptionLabel?(option: T): string;
  changed?(value: T | T[] | null): void;
}

export const AutocompleteWithPersistentAdd = <T,>({
  name,
  options,
  onCreate,
  optionLabelKey = 'label',
  creatable = false,
  persistentAddOption = false,
  label,
  required = false,
  placeholder = 'Search option...',
  multiple = false,
  disabled = false,
  getOptionLabel,
  changed,
}: AutocompleteWithPersistentAddProps<T>) => {
  const form = useFormContext();
  const autocompleteProps = useWsAutocomplete(
    getOptionLabel ?? optionLabelKey,
    creatable,
    persistentAddOption,
    label ?? placeholder
  );

  return (
    <>
      {label && <Label>{label}</Label>}
      <Controller
        control={form.control}
        name={name}
        render={({ field: { onChange, value: fieldValue } }) => (
          <Autocomplete
            value={fieldValue}
            size="small"
            filterOptions={autocompleteProps.filterOptions}
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            multiple={multiple}
            options={options}
            disabled={disabled}
            getOptionLabel={getOptionLabel ?? autocompleteProps.getOptionLabel}
            onChange={(event, value) => {
              const item = multiple ? (value as []).at(-1) : (value as Record<string, unknown>);
              if (item?.inputValue || item?.inputValue === '') {
                onCreate?.(item);
              } else {
                onChange(value);
                changed?.(value);
              }
            }}
            renderOption={autocompleteProps.renderOption}
            renderInput={(params) => (
              <TextField {...params} label={placeholder} {...(required && { required })} />
            )}
          />
        )}
      />
    </>
  );
};

const DrawerFooter = styled(Grid)(() => ({
  position: 'fixed',
  bottom: 1,
  justifyContent: 'revert',
  background: WsColors.TertiaryShades100,
  width: '50vw',
}));

const contractTypes = [
  {
    label: 'Permanent',
    value: 'PERMANENT',
  },
  {
    label: 'Contract',
    value: 'CONTRACT',
  },
  {
    label: 'Temporary',
    value: 'TEMPORARY',
  },
  {
    label: 'FullTime',
    value: 'FULLTIME',
  },
  {
    label: 'Internship',
    value: 'INTERNSHIP',
  },
  {
    label: 'ContractToHireW2',
    value: 'CONTACTTOHIREW2',
  },
  {
    label: 'ContractW2',
    value: 'CONTRACTW2',
  },
  {
    label: 'ContractIndependent',
    value: 'CONTRACTINDEPENDENT',
  },
  {
    label: 'ContractToHireIndependent',
    value: 'CONTRACTTOHIREINDEPENDENT',
  },
  {
    label: 'ContractCorpToCorp',
    value: 'CONTARCTCORPTOCORP',
  },
  {
    label: 'ContractToHireCorpToCorp',
    value: 'CONTARCTTOHIRECORPTOCORP',
  },
  {
    label: 'PartTime',
    value: 'PARTTIME',
  },
  {
    label: 'Freelance',
    value: 'FREELANCE',
  },
  {
    label: 'ThirdParty',
    value: 'THIRDPARTY',
  },
];

const empGuaranteePeriodUnits = [
  {
    label: 'Days',
    value: 'DAYS',
  },
  {
    label: 'Weeks',
    value: 'WEEKS',
  },
  {
    label: 'Months',
    value: 'MONTHS',
  },
];

const feeTypes = [
  {
    label: 'Percentage',
    value: 'PERCENTAGE',
  },
  {
    label: 'Fixed',
    value: 'FIXED',
  },
];

const contractfeeTypes = [
  {
    label: 'Percentage',
    value: 'PERCENTAGE',
  },
  {
    label: 'Fixed',
    value: 'FIXED',
  },
  {
    label: 'Transactional',
    value: 'TRANSACTIONAL',
  },
];

const periodOptions = [
  {
    label: 'Hourly',
    value: 'HOURLY',
  },
  {
    label: 'Weekly',
    value: 'WEEKLY',
  },
  {
    label: 'Monthly',
    value: 'MONTHLY',
  },
  {
    label: 'Annually',
    value: 'ANNUALLY',
  },
  {
    label: 'Once',
    value: 'ONCE',
  },
];

const serviceFeePeriod = [
  {
    label: 'Hourly',
    value: 'HOURLY',
  },
  {
    label: 'Daily',
    value: 'DAILY',
  },
  {
    label: 'Weekly',
    value: 'WEEKLY',
  },
  {
    label: 'Monthly',
    value: 'MONTHLY',
  },
  {
    label: 'Annually',
    value: 'ANNUALLY',
  },
  {
    label: 'Once',
    value: 'ONCE',
  },
];

interface IAddEditFeeArrangement<T> extends FormProps<T> {
  clientId: string;
  clientName?: string;
  id?: string;
  modalType?: string;
}

const feeArrangementDefaultValues = {
  clientContact: null,
  contractType: 'PERMANENT',
  empGuaranteePeriodUnit: 'DAYS',
  feeArrangementLevel: null,
  feeType: 'FIXED',
  startDate: null,
  endDate: null,
  currency: { id: '' },
  fee: '',
  ourEntityId: null,
  ourEntities: [],
  billRateExpense: 'FIXED',
  billRateFee: '',
  periodOptions: '',
  serviceFeePeriod: '',
};

const FeeArrangementForm = ({
  defaultValues = feeArrangementDefaultValues as unknown as IFeeArrangement,
  clientId,
  onCancel,
  onSave,
  id,
  modalType,
}: IAddEditFeeArrangement<IFeeArrangement>) => {
  const methods = useForm<IFeeArrangement>({ defaultValues });
  const [progress, setProgress] = useState<number>();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [clientContactOptions, setClientContactOptions] = useState([]);
  const [files, setFiles] = useState<IFeeArrangementFile[]>([]);
  const feeType = methods.watch('feeType');
  const billRateExpense = methods.watch('billRateExpense');
  const {
    reset,
    formState: { dirtyFields },
    watch,
  } = methods;
  const [ourEntities, setOurEntities] = useState<IOurEntity[]>([]);
  const [currencies] = useFetch(getCurrencyList);
  const contractStartDate = watch('startDate');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const arrangementType = watch('contractType');

  useEffect(() => {
    const fetchData = async () => {
      const response = await ourEntityAllList();
      setOurEntities(response.data.data);
    };
    fetchData();
  }, [setOurEntities]);

  useEffect(() => {
    setStartDate(contractStartDate);
  }, [contractStartDate]);

  useEffect(() => {
    (async () => {
      if (id) {
        const response = await getFeeArrangementById(id);
        setStartDate(response.data.startDate);
        reset(response.data);
        setFiles(response.data.feeArrangementFiles);
      }
    })();
  }, [reset, id]);

  const removeFile = async (index) => {
    const copy = [...files];
    await deleteFile(files[index].file.id);
    copy.splice(index, 1);
    setFiles(copy);
  };

  useEffect(() => {
    (async () => {
      if (defaultValues?.id) {
        const files = (await getAllArrangementFiles(defaultValues.id)).data;
        setFiles(files);
      }
    })();
  }, [clientId, defaultValues?.id]);

  const onUploadProgress = (progressEvent: ProgressEvent) => {
    const { loaded, total } = progressEvent;
    const percent = Math.floor((loaded * 100) / total);
    setProgress(percent);
  };

  const onFileUpload = async (file: File[]) => {
    try {
      const url = await uploadFileToS3(file[0], onUploadProgress);
      const response = await saveFile({ location: url.split('?')[0], name: file[0].name });
      setNotification(success('Uploaded file(s) successfully!'));
      setFiles((prevState) => [...prevState, { file: response.data }]);
    } catch {
      setNotification(failed('Failed to upload file'));
    }
    setProgress(undefined);
  };

  const handleSave = async (data) => {
    setIsLoading(true);
    const arrangement = {
      ...data,
      clientId,
      feeArrangementFiles: files,
    };
    try {
      let data;
      const avPayload = { ...activityLogDTOHelper };
      const avChangedValueViaDirtyFileds = Object.keys(dirtyFields).toString().split(',');
      if (defaultValues?.id) {
        ({ data } = await updateFeeArrangement(arrangement));
        const clientContact = arrangement.clientContact.firstName
          ? `${arrangement.clientContact.firstName} in`
          : '';
        const avEmptyEntityfields = Object.keys(
          Object.entries(defaultValues).reduce((acc, [k, v]) => {
            if (v === '' || v === null) acc[k] = v;
            return acc;
          }, {})
        );
        avPayload.action = LogAction.Edit;
        avPayload.description = `${getAvEditAddDesUiName(
          'Client',
          avEmptyEntityfields,
          avChangedValueViaDirtyFileds
        )} for ${clientContact} under`;
        setNotification(success(`Contract has been saved successfully !!`));
      } else {
        ({ data } = await createNewFeeArrangement(arrangement));
        avPayload.action = LogAction.Add;
        avPayload.description = `ADDED new Fee Arrangement for`;
        setNotification(success(`Contract has been added successfully !!`));
      }
      onSave(data);
      avPayload.entityType = LogEntityType.Fee;
      avPayload.parentEntitiyType = LogContextType.Client;
      avPayload.parentEntitiyId = clientId;
      avPayload.entityId = data.id;
      avPayload.field = `Fee Arrangements`;
      avPayload.contextData = { clientId: clientId };
      avChangedValueViaDirtyFileds[0].length > 0 ? await saveActivityLog(avPayload) : '';
    } catch (error: any) {
      if (error.response.status === 400)
        setNotification(warning(`Oops, contract already exists!!`));
      else setNotification(failed('Something went wrong !!'));
    }
    setIsLoading(false);
  };

  return (
    <>
      <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
        <FormProvider {...methods}>
          <Box component="form" onSubmit={methods.handleSubmit(handleSave)}>
            <Grid container spacing={2} sx={{ px: 2, pt: 1, pb: 12 }}>
              <Grid item xs={12} sm={6}>
                <Label>Arrangement Name*</Label>
                <ControlledTextInput
                  required
                  placeholder="Arrangement Name"
                  name="feeArrangementLevel"
                  fullWidth
                  size="small"
                  inputProps={{}}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <WsSelect
                  required
                  label="Arrangement Type*"
                  name="contractType"
                  selectArray={contractTypes}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {!id ? (
                  <>
                    <Label>Organization Entity*</Label>
                    <Controller
                      name="ourEntityIds"
                      control={methods.control}
                      defaultValue={[]}
                      render={({ field: { onChange, value = [] } }) => (
                        <Select
                          onChange={onChange}
                          value={value}
                          multiple
                          fullWidth
                          defaultValue={[]}
                          size="small"
                          renderValue={(selected) =>
                            selected
                              ?.map((id) => ourEntities?.find((oe) => oe.id === id)?.Name)
                              .join(', ') || 'Select some options'
                          }
                        >
                          {ourEntities?.map(({ id, Name }) => (
                            <MenuItem value={id} key={id}>
                              <Checkbox size="small" checked={value.includes(id as string)} />
                              <ListItemText primary={Name} />
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </>
                ) : (
                  <WsSelect
                    name="ourEntityId"
                    label="Organization Entity"
                    selectArray={
                      ourEntities?.map(({ id, Name }) => ({ label: Name, value: id as string })) ??
                      []
                    }
                  />
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <Label>Client Contact Name</Label>
                <Controller
                  control={methods.control}
                  name="clientContact"
                  render={({ field: { value, onChange } }) => (
                    <Autocomplete
                      value={value as ClientContactDTO}
                      size="small"
                      selectOnFocus
                      clearOnBlur={false}
                      handleHomeEndKeys
                      options={clientContactOptions as []}
                      getOptionLabel={getAutocompleteLabelForClientContact}
                      onChange={(_event, item) => onChange(item)}
                      onInputChange={async (_event, value) => {
                        const res = await getClientContactAutocompleteOptions(value, clientId);
                        const options = res.data;
                        options.forEach((option) => {
                          option.autocompleteLabel = getAutocompleteLabelForClientContact(option);
                          if (!option.id) {
                            option.id = option.value;
                          }
                        });
                        setClientContactOptions(options);
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Search client contact..." />
                      )}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label>Arrangement Start Date*</Label>
                <Controller
                  control={methods.control}
                  name="startDate"
                  render={({ field: { onChange, value = null } }) => (
                    <LocalizationProvider dateAdapter={DateAdapter}>
                      <DatePicker
                        value={value ?? null}
                        onChange={onChange}
                        renderInput={(params) => (
                          <TextField size="small" fullWidth {...params} required />
                        )}
                        inputFormat="dd/MM/yyyy"
                      />
                    </LocalizationProvider>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label>Arrangement End Date*</Label>
                <WsDatePicker
                  name="endDate"
                  label=""
                  {...(startDate && { minDate: new Date(startDate) })}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <Label>Notice Period (Days)</Label>
                <ControlledTextInput name="noticePeriod" fullWidth size="small" inputProps={{}} />
              </Grid>
              <Grid item xs={6}>
                <WsInputNumber name="paymentTerms" label="Payment Terms (Days)" />
              </Grid>
              {arrangementType === 'PERMANENT' && (
                <>
                  <Grid item xs={6} sm={6}>
                    <Label>Employment Guarantee*</Label>
                    <Stack direction="row" spacing={1}>
                      <ControlledTextInput
                        fullWidth
                        type="number"
                        name="employmentGuarantee"
                        placeholder="Employment Guarantee"
                        size="small"
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        required
                        showMandatorySymbol={true}
                      />
                      <WsSelect
                        required
                        name="empGuaranteePeriodUnit"
                        selectArray={empGuaranteePeriodUnits}
                      />
                    </Stack>
                  </Grid>
                </>
              )}
              <Grid item xs={6}>
                <WsInputNumber name="invoiceTerms" label="Invoicing Term(Days)" />
              </Grid>
              <Grid item xs={12} sm={12}>
                <Label>Service Fees*</Label>
                <Stack direction="row" spacing={1}>
                  <Controller
                    control={methods.control}
                    name="feeType"
                    render={({ field: { onChange, value } }) => (
                      <WsToggleButton
                        buttonGroupPadding={6}
                        buttonsList={arrangementType === 'PERMANENT' ? feeTypes : contractfeeTypes}
                        defaultValue={value ?? 'FIXED'}
                        selectedIndexValue={onChange}
                      />
                    )}
                  />
                  <Box sx={{ flexGrow: 1 }}>
                    {feeType === 'PERCENTAGE' && (
                      <Controller
                        control={methods.control}
                        name="fee"
                        render={({ field: { value, onChange }, fieldState }) => (
                          <Autocomplete
                            value={String(value)}
                            freeSolo
                            size="small"
                            selectOnFocus
                            handleHomeEndKeys
                            options={['5', '10']}
                            onInputChange={(event, value) => onChange(value)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                required
                                label="Service Fees"
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                              />
                            )}
                          />
                        )}
                      />
                    )}
                    {feeType === 'FIXED' && (
                      <Stack direction="row" spacing={1}>
                        <WsSelect
                          placeholder="Currency"
                          name="currency.id"
                          required
                          selectArray={
                            currencies?.map((currency) => ({
                              label: currency?.symbol,
                              value: currency?.id,
                            })) ?? []
                          }
                          size="small"
                        />
                        <ControlledTextInput
                          fullWidth
                          name="fee"
                          required
                          placeholder="Service Fees*"
                          size="small"
                          inputProps={{}}
                        />
                        <WsSelect
                          required
                          placeholder="Period"
                          name="serviceFeePeriod"
                          selectArray={serviceFeePeriod}
                          size="small"
                        />
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Grid>

              {arrangementType !== 'PERMANENT' && (
                <>
                  <Grid item xs={12} sm={8}>
                    <Label>Bill Rate Expenses</Label>
                    <Stack direction="row" spacing={1}>
                      <Controller
                        control={methods.control}
                        name="billRateExpense"
                        render={({ field: { onChange, value } }) => (
                          <WsToggleButton
                            buttonGroupPadding={6}
                            buttonsList={feeTypes}
                            defaultValue={value ?? 'FIXED'}
                            selectedIndexValue={onChange}
                          />
                        )}
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        {billRateExpense === 'PERCENTAGE' && (
                          <Controller
                            control={methods.control}
                            name="billRateFee"
                            render={({ field: { value, onChange }, fieldState }) => (
                              <Autocomplete
                                value={String(value)}
                                freeSolo
                                size="small"
                                selectOnFocus
                                handleHomeEndKeys
                                options={['5', '10']}
                                onInputChange={(event, value) => onChange(value)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Bill Rate Expenses"
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                  />
                                )}
                              />
                            )}
                          />
                        )}
                        {billRateExpense === 'FIXED' && (
                          <Stack direction="row" spacing={1}>
                            <WsSelect
                              placeholder="Currency"
                              name="currency.symbol"
                              selectArray={
                                currencies?.map((currency) => ({
                                  label: currency?.symbol,
                                  value: currency?.id,
                                })) ?? []
                              }
                              size="small"
                            />
                            <ControlledTextInput
                              fullWidth
                              name="billRateFee"
                              placeholder="Service Fees"
                              size="small"
                              inputProps={{}}
                            />
                            <WsSelect
                              placeholder="Period"
                              name="period"
                              selectArray={periodOptions}
                              size="small"
                              showMandatorySymbol
                            />
                          </Stack>
                        )}
                      </Box>
                    </Stack>
                  </Grid>
                </>
              )}
              {/* <Grid item xs={6}>
                <WsInputNumber name="invoiceTerms" label="Invoicing Term(Days)" />
              </Grid> */}
              <Grid item xs={12}>
                <ControlledTextArea name="comments" labelText="Comments" minRows={4} />
              </Grid>
              {modalType != 'modal' && (
                <Grid item xs={12}>
                  {files.map((feeArrFile, index) => (
                    <Stack key={index} direction="row" spacing={2} mb={1}>
                      <WsTypography variant={WsTypoVariants.BODY1}>
                        {feeArrFile.file.name ?? ''}
                      </WsTypography>
                      <CloseIcon
                        onClick={() => removeFile(index)}
                        sx={{ color: WsColors.Red500, cursor: 'pointer' }}
                      />
                    </Stack>
                  ))}
                  <WsFileUpload
                    multiple={false}
                    onUpload={onFileUpload}
                    name="uploadFiles"
                    placeholderText="Upload PDF, Document and Image files"
                    bgRequired={files.length === 0}
                    buttonText={'Upload File'}
                    fileTypes={FileTypeVariants.DOCS_AND_IMAGES}
                  />
                  {progress && (
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress variant="determinate" value={progress} />
                    </Box>
                  )}
                </Grid>
              )}
            </Grid>
            {modalType === 'modal' ? (
              <Stack direction="row" justifyContent="flex-end" m={2} sx={{ mt: -10 }} spacing={2}>
                <Button size="large" color="error" onClick={onCancel}>
                  Cancel
                </Button>
                <WsButton size="large" endIcon={<ChevronRight />} type="submit">
                  Save
                </WsButton>
              </Stack>
            ) : (
              <DrawerFooter>
                <Stack direction="row" justifyContent="center" m={2} pr={6} spacing={2}>
                  <WsButton size="large" onClick={onCancel}>
                    Cancel
                  </WsButton>
                  <WsButton
                    size="large"
                    type="submit"
                    disabled={isLoading}
                    endIcon={
                      isLoading ? (
                        <CircularProgress size={17} color="secondary" />
                      ) : (
                        <ChevronRight />
                      )
                    }
                  >
                    {isLoading ? 'Saving...' : 'Save'}
                  </WsButton>
                </Stack>
              </DrawerFooter>
            )}
          </Box>
        </FormProvider>
      </ErrorBoundary>
    </>
  );
};

export const CurrencyDropdown = ({ name }: { name: string }) => {
  const [currencies, setCurrencies] = useState([]);

  useEffect(() => {
    (async () => {
      const response = await getCurrencyList();
      setCurrencies(
        response.data.map((currency) => ({
          label: currency?.symbol,
          value: currency?.id,
        }))
      );
    })();
  }, []);

  return <WsSelect placeholder="Currency" name={name} selectArray={currencies} size="small" />;
};

export default FeeArrangementForm;
